// W.Sign API client — talks to api.sign.wsoft.tr (prod) or the Vite proxy (dev).
//
// All endpoints documented at api.sign.wsoft.tr/swagger (Development env only).
// Verify endpoint is anonymous; sign endpoints require X-WSign-Api-Key.

const API_BASE = import.meta.env.PROD
  ? 'https://api.sign.wsoft.tr'
  : ''  // dev: Vite proxy intercepts /v1/*

// Köprü (ayrı bir yerel süreç) API_BASE'i kendisi çağırır; bu yüzden ona her zaman
// MUTLAK bir URL geçilmeli. Dev'de API_BASE boş olduğundan canlı API'ye düşülür.
export const SERVER_BASE_URL = API_BASE || 'https://api.sign.wsoft.tr'

export interface VerifyResponse {
  isValid:      boolean
  format:       string
  status:       string
  signers:      VerifySignerInfo[]
  errorMessage: string | null
  /** PAdES only — document-level facts (level, revisions, DSS). Null for CAdES / XAdES. */
  document?:           VerifyDocumentInfo | null
  /** PAdES only — ETSI.RFC3161 document timestamps (PAdES-B-LTA archival). */
  documentTimestamps?: VerifyDocumentTimestampInfo[] | null
}

export interface VerifySignerInfo {
  subjectName:                      string
  issuerName:                       string
  serialNumber:                     string
  thumbprint:                       string
  digestAlgorithm:                  string
  notBefore:                        string
  notAfter:                         string
  signatureValid:                   boolean
  messageDigestValid:               boolean
  signingCertificateReferenceValid: boolean
  signingTime:                      string | null
  timestamp:                        VerifyTimestampInfo | null
  certificateBase64Der:             string | null
  /** CAdES-XL / -ESXLong material carried inside the CMS itself. */
  longTerm?:                        VerifySignerLongTermInfo | null
  /** Which PDF signature field this signer came from. Null for CAdES / XAdES. */
  pdf?:                             VerifyPdfSignatureInfo | null
}

export interface VerifyTimestampInfo {
  time:                string
  tsaSubject:          string | null
  messageImprintValid: boolean
  tsaSignatureValid:   boolean
}

export interface VerifySignerLongTermInfo {
  embeddedCertificateCount:  number
  embeddedCrlCount:          number
  embeddedOcspResponseCount: number
  /** 'Good' | 'Revoked' | 'Unknown' */
  revocationStatus:          string
}

export interface VerifyPdfSignatureInfo {
  fieldName:           string
  subFilter:           string | null
  revision:            number
  /** False when a revision was appended after this signature (DSS or another signature). */
  coversWholeDocument: boolean
  /** False when the file holds bytes that are neither signed nor the signature placeholder. */
  byteRangeConsistent: boolean
  signedByteCount:     number
  signatureDate:       string | null
  reason:              string | null
  location:            string | null
  signerName:          string | null
}

export interface VerifyDocumentInfo {
  /** 'PAdES-B-B' | 'PAdES-B-T' | 'PAdES-B-LT' | 'PAdES-B-LTA' */
  level:                          string
  signatureCount:                 number
  documentTimestampCount:         number
  totalRevisions:                 number
  lastRevisionCoveredBySignature: boolean
  dss:                            VerifyDssInfo | null
}

export interface VerifyDssInfo {
  certificateCount:  number
  crlCount:          number
  ocspResponseCount: number
  vriKeys:           string[]
}

export interface VerifyDocumentTimestampInfo {
  fieldName:           string
  revision:            number
  isValid:             boolean
  coversWholeDocument: boolean
  time:                string | null
  tsaSubject:          string | null
  messageImprintValid: boolean
  tsaSignatureValid:   boolean
  errorMessage:        string | null
}

export interface VerifyError {
  error:   string
  message: string
}

export interface CertificateInfo {
  alias:        string
  subject:      string
  issuer:       string
  serialNumber: string
  thumbprint:   string
  notBefore:    string
  notAfter:     string
  isDefault:    boolean
  isDemo:       boolean
}

export interface CertificatesResponse {
  defaultAlias: string | null
  count:        number
  certificates: CertificateInfo[]
}

export interface SignCadesResponse {
  signedCms:        string
  format:           string
  profile:          string
  signerThumbprint: string
  signerSubject:    string
}

export interface SignXadesResponse {
  signedXml:        string
  format:           string
  variant:          string
  signerThumbprint: string
  signerSubject:    string
}

export interface HashPrepareResponse {
  sessionId:  string
  dataToSign: string
  ttlSeconds: number
}

export interface HashCompleteResponse {
  signedCms:         string
  signerCertificate: string
}

// ── Redirect-session (ADR-0006) — 3D-Secure tarzı imza akışı ──────────────────
//
// Tarayıcı `/s/{id}` sayfası bu iki uca konuşur (her ikisi de session-bearer,
// API key YOK): GET ile durum/metadata çeker, DELETE ile iptal eder. Belge ve
// dataToSign hiçbir zaman tarayıcıya inmez — onları yalnızca Desktop çeker.

export type RedirectSignStatus =
  | 'pending'           // oluşturuldu; kullanıcı henüz Desktop'ı tetiklemedi
  | 'awaitingSignature' // Desktop /prepare çağırdı; kullanıcı sertifika/PIN giriyor
  | 'signing'           // imza tamamlanıyor
  | 'completed'         // CMS hazır; successRedirectUrl'e gidilir
  | 'cancelled'         // iptal edildi
  | 'expired'           // TTL doldu

export interface RedirectSignInfo {
  sessionId:    string
  status:       RedirectSignStatus
  documentName: string | null
  /** Oturumu başlatan entegratörün origin'i (ör. https://acme.example). */
  origin:       string | null
  expiresAt:    string | null
  /**
   * Tamamlanınca sonucun entegratöre nasıl teslim edileceği:
   * - 'redirect' (varsayılan): tarayıcı `successRedirectUrl`'e yönlendirilir.
   * - 'post': kapalı sistemler için; tarayıcı sunucunun `deliver` ucuna gider,
   *   sunucu sonucu entegratörün returnUrl'ine otomatik-POST eden bir HTML form döner
   *   (3DS termUrl kalıbı). Sunucu alanı döndürmezse 'redirect' kabul edilir.
   */
  returnMode?: 'redirect' | 'post'
  // ADR-0006 §"Endpoint Sözleşmesi v1" endpoint (2) bu iki alanı RESMEN döndürmez
  // (yönlendirme URL'leri hassas kabul ediliyor). Faz 0b sayfa sözleşmesi bunların
  // GET info'ya eklenmesini öngörüyor; sunucu döndürmezse sayfa graceful davranır
  // (yönlendirme yerine bilgi mesajı gösterir).
  successRedirectUrl?: string | null
  cancelRedirectUrl?:  string | null
}

class ApiClient {
  private apiKey: string | null = null

  setApiKey(key: string | null) {
    this.apiKey = key && key.trim().length > 0 ? key.trim() : null
  }

  /** Verify a signed document — anonymous, public verify portal. */
  async verifyFile(file: File): Promise<VerifyResponse> {
    const form = new FormData()
    form.append('file', file)

    const resp = await fetch(`${API_BASE}/v1/verify/upload`, {
      method: 'POST',
      body: form,
    })

    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /** Verify via JSON body — same shape; useful when caller already has base64. */
  async verifyBase64(contentBase64: string, detachedBase64?: string): Promise<VerifyResponse> {
    const resp = await fetch(`${API_BASE}/v1/verify`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        content:           contentBase64,
        detachedSignature: detachedBase64,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /** GET /v1/certs/list — requires API key. */
  async listCertificates(): Promise<CertificatesResponse> {
    const resp = await fetch(`${API_BASE}/v1/certs/list`, {
      headers: this.authHeaders(),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /** POST /v1/sign/cades — full document, server PFX. */
  async signCades(opts: {
    contentBase64:    string
    certificateAlias?: string
    profile?:          'BES' | 'T'
    detached?:         boolean
    digestAlgorithm?:  'SHA256' | 'SHA384' | 'SHA512'
  }): Promise<SignCadesResponse> {
    const resp = await fetch(`${API_BASE}/v1/sign/cades`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body:    JSON.stringify({
        content:          opts.contentBase64,
        certificateAlias: opts.certificateAlias,
        profile:          opts.profile,
        detached:         opts.detached ?? false,
        digestAlgorithm:  opts.digestAlgorithm,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * POST /v1/sign/timestamp — CAdES-BES → CAdES-T. Bridge BES üretir, sunucu TSA
   * çağırır. Demo modda anonim + IP rate-limited.
   */
  async addTimestamp(opts: {
    cadesBesBase64:        string
    imprintHashAlgorithm?: 'SHA256' | 'SHA384' | 'SHA512'
  }): Promise<{ cadesT: string; format: string }> {
    const resp = await fetch(`${API_BASE}/v1/sign/timestamp`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body:    JSON.stringify({
        cadesBes:             opts.cadesBesBase64,
        imprintHashAlgorithm: opts.imprintHashAlgorithm,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /** POST /v1/sign/xades — full XML signature, server PFX. */
  async signXades(opts: {
    contentBase64:    string
    certificateAlias?: string
    variant?:          'Enveloped' | 'Enveloping' | 'Detached'
    digestAlgorithm?:  'SHA256' | 'SHA384' | 'SHA512'
    documentUri?:      string
  }): Promise<SignXadesResponse> {
    const resp = await fetch(`${API_BASE}/v1/sign/xades`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body:    JSON.stringify({
        content:          opts.contentBase64,
        certificateAlias: opts.certificateAlias,
        variant:          opts.variant,
        digestAlgorithm:  opts.digestAlgorithm,
        documentUri:      opts.documentUri,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * Phase 1 of client-side hash-only signing — server builds CMS SignedAttributes
   * and returns the bytes to sign. Demo public mode allows anonymous calls
   * (5/saat/IP). Caller supplies user's certificate (DER Base64) so server can
   * hash signing-certificate-v2 attribute.
   */
  async hashSignPrepare(opts: {
    contentBase64:        string
    signerCertificateB64: string
    algorithm?:           'CAdES-BES' | 'CAdES-T' | 'XAdES-B-B'
    digestAlgorithm?:     'SHA256' | 'SHA384' | 'SHA512'
  }): Promise<HashPrepareResponse> {
    const resp = await fetch(`${API_BASE}/v1/sign/hash/prepare`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body:    JSON.stringify({
        algorithm:         opts.algorithm ?? 'CAdES-BES',
        content:           opts.contentBase64,
        signerCertificate: opts.signerCertificateB64,
        digestAlgorithm:   opts.digestAlgorithm,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * Phase 2 — client returns the raw PKCS#1 signature it produced over dataToSign,
   * server completes the CMS frame and returns the final signed blob.
   */
  async hashSignComplete(opts: {
    sessionId:       string
    rawSignatureB64: string
  }): Promise<HashCompleteResponse> {
    const resp = await fetch(`${API_BASE}/v1/sign/hash/complete`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body:    JSON.stringify({
        sessionId:    opts.sessionId,
        rawSignature: opts.rawSignatureB64,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * GET /v1/redirect-sign/sessions/{id} — redirect-session durumu + sayfa metadata.
   * Anonim (session-bearer); `sessionId` taşıyıcıdır, API key gerekmez. Bilinmeyen/
   * süresi dolmuş oturumda `404` döner.
   */
  async redirectSignInfo(sessionId: string): Promise<RedirectSignInfo> {
    const resp = await fetch(
      `${API_BASE}/v1/redirect-sign/sessions/${encodeURIComponent(sessionId)}`,
    )
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * DELETE /v1/redirect-sign/sessions/{id} — kullanıcı iptali (session-bearer).
   * Sunucu `cancelRedirectUrl`'e callback POST'lar ve `{status:"cancelled"}` döner.
   */
  async redirectSignCancel(sessionId: string): Promise<{ status: string }> {
    const resp = await fetch(
      `${API_BASE}/v1/redirect-sign/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' },
    )
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as VerifyError | null
      throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  private authHeaders(): Record<string, string> {
    return this.apiKey ? { 'X-WSign-Api-Key': this.apiKey } : {}
  }
}

// ── Bridge (localhost W.Sign Desktop) client ──────────────────────────────────

const BRIDGE_HTTP_PORTS = [13013, 13014, 13015]
const BRIDGE_HTTPS_PORTS = [13113, 13114, 13115]
// A record → 127.0.0.1, served with a publicly-trusted Let's Encrypt cert (ADR-0012).
const BRIDGE_HTTPS_HOST = 'helper.sign.wsoft.tr'

/**
 * Bridge base URLs to probe, in order. On an https:// page a browser blocks a fetch
 * to http://127.0.0.1 as mixed content, so the loopback bridge is reached over TLS via
 * helper.sign.wsoft.tr:13113 — a public name that resolves to 127.0.0.1 with a real
 * certificate, so no warning and no local root install. On plain http (dev) the direct
 * loopback http URL is fine and needs no certificate.
 */
function bridgeBaseUrls(): string[] {
  const isHttps = typeof location !== 'undefined' && location.protocol === 'https:'
  return isHttps
    ? BRIDGE_HTTPS_PORTS.map(p => `https://${BRIDGE_HTTPS_HOST}:${p}`)
    : BRIDGE_HTTP_PORTS.map(p => `http://127.0.0.1:${p}`)
}

/**
 * Fire a custom-protocol URL (e.g. `wsign://wake`) without navigating the page.
 * A transient hidden iframe is the conventional safe trigger: if the protocol is
 * registered Windows launches the handler; if not, browsers ignore it silently
 * (unlike `location.href`, which can flash a "no app" error page). Must be called
 * from within a user gesture — Chrome shows a permission prompt the first time.
 */
export function triggerProtocol(url: string): void {
  const frame = document.createElement('iframe')
  frame.style.display = 'none'
  frame.src = url
  document.body.appendChild(frame)
  setTimeout(() => frame.remove(), 1000)
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export interface BridgeStatus {
  running:        boolean
  version:        string
  port:           number
  corsAllowList:  string[]
  bridgeBaseUrl:  string   // discovered base URL (e.g. http://127.0.0.1:13013)
}

export interface BridgeTokenInfo {
  slotId:       number
  slotDesc:     string
  label:        string
  manufacturer: string
  model:        string
  serial:       string
}

export interface BridgeDriverInfo {
  driver:         string
  path:           string
  available:      boolean
  error?:         string
  /** True when the driver enumerated at least one reader slot (reader plugged in). */
  readerPresent?: boolean
  /** Human-readable reason a driver yielded no usable token (e.g. reader but no card). */
  note?:          string | null
  tokens:         BridgeTokenInfo[]
}

/** Full /v1/tokens body: per-driver list plus a top-level empty-state summary. */
export interface BridgeTokensResult {
  count:      number
  tokenFound: boolean
  message:    string | null
  drivers:    BridgeDriverInfo[]
}

export interface BridgeCertInfo {
  ckaId:           string
  label:           string | null
  hasPrivateKey:   boolean
  subject:         string
  issuer:          string
  serialNumber:    string
  thumbprint:      string
  notBefore:       string
  notAfter:        string
  certificateB64:  string
}

export interface BridgeSignResult {
  signedBytes:     string
  format:          string
  variant:         string
  signerSubject:   string
  signerThumbprint: string
}

/**
 * Köprünün redirect-session imza akışına verdiği yanıt. Köprü oturumu kendi PIN
 * diyaloğuyla imzalar ve tamamlanınca `successRedirectUrl`'i de döndürür (sunucunun
 * GET info'su bu alanı hassas kabul edip döndürmeyebilir — bu yüzden köprü yanıtı
 * yönlendirme için güvenilir bir kaynaktır).
 */
export interface BridgeRedirectSignResponse {
  status:              string
  successRedirectUrl?: string | null
}

export class BridgeClient {
  private _baseUrl: string | null = null
  get baseUrl() { return this._baseUrl }

  /**
   * Probe the bridge base URLs in order (HTTPS helper on an https page, direct http
   * loopback on dev); first base returning 200 from /api/status wins.
   * Returns null when Bridge is not running locally.
   */
  async probe(): Promise<BridgeStatus | null> {
    for (const base of bridgeBaseUrls()) {
      try {
        const resp = await fetch(`${base}/api/status`, {
          method: 'GET',
          // Cross-origin from https://sign.wsoft.tr → the bridge (helper.sign.wsoft.tr
          // over TLS, or http://127.0.0.1 in dev) — Bridge must emit
          // Access-Control-Allow-Origin for our origin; otherwise this fetch rejects.
          mode: 'cors',
          credentials: 'omit',
        })
        if (resp.ok) {
          const status = (await resp.json()) as Omit<BridgeStatus, 'bridgeBaseUrl'>
          this._baseUrl = base
          return { ...status, bridgeBaseUrl: base }
        }
      } catch {
        // Try next base
      }
    }
    this._baseUrl = null
    return null
  }

  /**
   * Wake an installed-but-not-running bridge via the `wsign://wake` protocol, then poll
   * {@link probe} until it comes up or the timeout elapses. Returns the {@link BridgeStatus}
   * once reachable, or null if no handler responded in time (not installed, or launch denied).
   * Call from a user gesture so the browser allows the protocol launch.
   */
  async wakeAndWait(opts?: { timeoutMs?: number; intervalMs?: number }): Promise<BridgeStatus | null> {
    const timeoutMs  = opts?.timeoutMs  ?? 8000
    const intervalMs = opts?.intervalMs ?? 600

    triggerProtocol('wsign://wake')

    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      await sleep(intervalMs)
      const status = await this.probe()
      if (status) return status
    }
    return null
  }

  async listTokens(): Promise<BridgeDriverInfo[]> {
    return (await this.listTokensFull()).drivers
  }

  /** Full /v1/tokens body including the top-level empty-state {@link BridgeTokensResult.message}. */
  async listTokensFull(): Promise<BridgeTokensResult> {
    if (!this._baseUrl) throw new Error('Bridge bulunamadı; önce probe() çağır.')
    const resp = await fetch(`${this._baseUrl}/v1/tokens`)
    if (!resp.ok) throw new Error(`Bridge /v1/tokens döndü: ${resp.status}`)
    const body = await resp.json() as Partial<BridgeTokensResult> & { drivers: BridgeDriverInfo[] }
    return {
      count:      body.count ?? body.drivers.length,
      tokenFound: body.tokenFound ?? body.drivers.some(d => d.tokens.length > 0),
      message:    body.message ?? null,
      drivers:    body.drivers,
    }
  }

  async listCertificates(opts: { driverPath: string; slotId: number; pin: string }):
    Promise<BridgeCertInfo[]>
  {
    if (!this._baseUrl) throw new Error('Bridge bulunamadı.')
    const resp = await fetch(`${this._baseUrl}/v1/tokens/certificates`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(opts),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
      throw new ApiError(resp.status, body?.error ?? 'BRIDGE_ERROR', body?.message ?? resp.statusText)
    }
    const body = await resp.json() as { count: number; certificates: BridgeCertInfo[] }
    return body.certificates
  }

  async sign(opts: {
    driverPath:    string
    slotId:        number
    pin:           string
    ckaId:         string
    contentBase64: string
    algorithm?:    'CAdES-BES' | 'XAdES-B-B'
    detached?:     boolean
    xadesVariant?: 'Enveloped' | 'Enveloping' | 'Detached'
    documentUri?:  string
  }): Promise<BridgeSignResult> {
    if (!this._baseUrl) throw new Error('Bridge bulunamadı.')
    const resp = await fetch(`${this._baseUrl}/v1/tokens/sign`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        driverPath:    opts.driverPath,
        slotId:        opts.slotId,
        pin:           opts.pin,
        ckaId:         opts.ckaId,
        contentBase64: opts.contentBase64,
        algorithm:     opts.algorithm ?? 'CAdES-BES',
        detached:      opts.detached ?? true,
        xadesVariant:  opts.xadesVariant,
        documentUri:   opts.documentUri,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
      throw new ApiError(resp.status, body?.error ?? 'BRIDGE_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * Redirect-session (ADR-0006) yerel imza akışı. `probe()` ile keşfedilmiş köprüye
   * `POST /v1/redirect/sign` atar; köprü oturumun belgesini sunucudan kendisi çeker,
   * kendi PIN diyaloğunu gösterir ve imzayı tamamlayınca sonucu döndürür.
   *
   * Bu çağrı kasıtlı olarak UZUN ÖMÜRLÜDÜR (kullanıcı PIN girene kadar bekler), bu
   * yüzden timeout konmaz; sayfa bu sırada durumu poll etmeye devam eder. Hızlı
   * "köprü var mı?" kararı {@link probe} ile (kısa) ayrı verilir.
   *
   * Hatalar `ApiError` olarak fırlatılır: 409 BUSY / SESSION_NOT_SIGNABLE,
   * 422 NO_TOKEN, 400/502.
   */
  async redirectSign(opts: { sessionId: string; serverBaseUrl: string }):
    Promise<BridgeRedirectSignResponse>
  {
    if (!this._baseUrl) throw new Error('Bridge bulunamadı; önce probe() çağır.')
    const resp = await fetch(`${this._baseUrl}/v1/redirect/sign`, {
      method:      'POST',
      mode:        'cors',
      credentials: 'omit',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({
        sessionId:     opts.sessionId,
        serverBaseUrl: opts.serverBaseUrl,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
      throw new ApiError(resp.status, body?.error ?? 'BRIDGE_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }

  /**
   * Hash-only companion: sign server-prepared `dataToSign` bytes with the token, returning
   * the raw PKCS#1 signature. Used by the portal flows where the server holds the document
   * and assembles the final CMS (POST /v1/portal/.../complete).
   */
  async signHash(opts: {
    driverPath:       string
    slotId:           number
    pin:              string
    ckaId:            string
    dataToSignBase64: string
    digestAlgorithm?: 'SHA256' | 'SHA384' | 'SHA512'
  }): Promise<{ rawSignature: string; signerCertificate: string; signerSubject: string }> {
    if (!this._baseUrl) throw new Error('Bridge bulunamadı.')
    const resp = await fetch(`${this._baseUrl}/v1/tokens/sign-hash`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        driverPath:       opts.driverPath,
        slotId:           opts.slotId,
        pin:              opts.pin,
        ckaId:            opts.ckaId,
        dataToSignBase64: opts.dataToSignBase64,
        digestAlgorithm:  opts.digestAlgorithm,
      }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
      throw new ApiError(resp.status, body?.error ?? 'BRIDGE_ERROR', body?.message ?? resp.statusText)
    }
    return resp.json()
  }
}

export const bridge = new BridgeClient()

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export const api = new ApiClient()

// ── File helpers ─────────────────────────────────────────────────────────────

/** Read a File into a Base64 string. Used by drop-zone components. */
export async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buf)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)))
  }
  return btoa(binary)
}

/** Decode Base64 into a Blob the user can download. */
export function base64ToBlob(base64: string, mime = 'application/octet-stream'): Blob {
  const binary = atob(base64)
  const bytes  = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Trigger a browser download for a Blob with the given filename. */
export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a   = document.createElement('a')
  a.href     = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
